import cn from "classnames";
import { useProfile } from "modules/common";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Loader } from "ui";

import { EAiAssistantChannel } from "../../aiAgents.types";
import {
  useGetAssistants,
  useGetAssistantStats,
  useUpdateAssistant
} from "../../apis";
import { AgentAvatar } from "../../components/AgentAvatar";
import { ChannelsTab } from "../../components/ChannelsTab";
import { InstructionsTab } from "../../components/InstructionsTab";
import { KnowledgeTab } from "../../components/KnowledgeTab";
import { LessonsTab } from "../../components/LessonsTab";
import { PromptTab } from "../../components/PromptTab";
import { Sandbox } from "../../components/Sandbox";
import { ToolsTab } from "../../components/ToolsTab";
import { assistantSupportsLessons } from "../../helpers/lessons";

import styles from "./AIAgentDetail.module.scss";

const BackArrow = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M14 18l-6-6 6-6"
      stroke="#090914"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHANNEL_COLOR: Record<EAiAssistantChannel, string> = {
  [EAiAssistantChannel.WhatsApp]: "#733FB7",
  [EAiAssistantChannel.InAppSupport]: "#03BF9D"
};

const PLACEHOLDER = "—";

const formatCount = (value: number | undefined) =>
  typeof value === "number" ? value.toLocaleString() : PLACEHOLDER;

const formatRate = (value: number | undefined) =>
  typeof value === "number" ? `${Math.round(value * 100)}%` : PLACEHOLDER;

type Tab =
  | "Tools"
  | "Channels"
  | "Instructions"
  | "Prompt"
  | "Knowledge"
  | "Lessons";

const AIAgentDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const { user } = useProfile({ enabled: false });
  const isSuperAdmin = user.role === "Super";

  const { assistants, isLoading } = useGetAssistants();
  const { stats } = useGetAssistantStats(agentId);
  const updateMutation = useUpdateAssistant();

  const [activeTab, setActiveTab] = useState<Tab>("Tools");

  const assistant = assistants.find((a) => a.id === agentId);

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  if (!assistant) {
    return (
      <div className={styles.page}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate("/ai/agents")}
        >
          <BackArrow />
          <span>{t("AI Agents")}</span>
        </button>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "Tools", label: t("Tools") },
    { key: "Channels", label: t("Channels") },
    { key: "Instructions", label: t("Instructions") },
    { key: "Prompt", label: t("Prompt") },
    { key: "Knowledge", label: t("Knowledge") },
    // WhatsApp sales assistant only (v1), and only once the backend serves
    // lessons.
    ...(assistantSupportsLessons(assistant)
      ? [{ key: "Lessons" as const, label: t("aiLesson.tabLabel") }]
      : [])
  ];

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate("/ai/agents")}
        >
          <BackArrow />
          <span>{t("AI Agents")}</span>
        </button>

        <div className={styles.aiStatusBlock}>
          <div className={styles.aiStatusText}>
            <div className={styles.aiStatusTitle}>
              {assistant.isActive
                ? t("AI agent is active")
                : t("AI agent is inactive")}
            </div>
            <div className={styles.aiStatusSub}>
              {assistant.isActive
                ? t("Switch to disable")
                : t("Switch to enable")}
            </div>
          </div>
          <button
            type="button"
            className={cn(styles.switch, {
              [styles.switchActive]: assistant.isActive
            })}
            disabled={updateMutation.isLoading}
            onClick={() =>
              updateMutation.mutate({
                assistantId: assistant.id,
                isActive: !assistant.isActive
              })
            }
            aria-label="Toggle AI agent"
          >
            <span className={styles.switchKnob} />
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.agentInfo}>
            <AgentAvatar
              letter={(assistant.name[0] ?? "A").toUpperCase()}
              color={CHANNEL_COLOR[assistant.channel]}
              size={48}
            />
            <div className={styles.agentInfoText}>
              <div className={styles.agentName}>{assistant.name}</div>
              <div className={styles.agentDesc}>
                {t("Assistant dedicated to customer support")}
              </div>
            </div>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <div className={styles.statValue}>
                  {formatCount(stats?.convos)}
                </div>
                <div className={styles.statLabel}>{t("Convos")}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statValue}>
                  {formatRate(stats?.resolvedRate)}
                </div>
                <div className={styles.statLabel}>{t("Resolved")}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statValue}>
                  {formatRate(stats?.handoffRate)}
                </div>
                <div className={styles.statLabel}>{t("Handoff")}</div>
              </div>
            </div>
          </div>

          <Sandbox assistant={assistant} />
        </div>

        <div className={styles.right}>
          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={cn(styles.tab, {
                  [styles.tabActive]: tab.key === activeTab
                })}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {activeTab === "Tools" && <ToolsTab assistantId={assistant.id} />}
            {activeTab === "Channels" && (
              <ChannelsTab channel={assistant.channel} />
            )}
            {activeTab === "Instructions" && (
              <InstructionsTab assistantId={assistant.id} />
            )}
            {activeTab === "Prompt" && (
              <PromptTab assistantId={assistant.id} canEdit={isSuperAdmin} />
            )}
            {activeTab === "Knowledge" && (
              <KnowledgeTab assistantId={assistant.id} />
            )}
            {activeTab === "Lessons" && assistantSupportsLessons(assistant) && (
              <LessonsTab assistantId={assistant.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgentDetail;
